import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DatePicker } from 'syngenta-digital-cropwise-react-ui-kit';
import moment, { Moment } from 'moment';

interface MultiFormatDateTimePickerProps {
  value: Moment | null;
  onChange: (value: Moment | null) => void;
  onBlur?: () => void;
  placeholder?: string;
  format?: string;
  showTime?: boolean | object;
  allowClear?: boolean;
  style?: React.CSSProperties;
  name?: string;
  'data-testid'?: string;
  getPopupContainer?: (node: HTMLElement) => HTMLElement;
}

// Supported input formats for manual text entry (12-hour format only, no space before AM/PM)
// Covers variations where month, day, and hour can have 1 or 2 digits (minutes must be 2 digits)
const ACCEPTED_FORMATS = [
  // 12-hour format with AM/PM (2-digit month/day, 2-digit hour:minute)
  'MM-DD-YYYY hh:mmA',
  'MM/DD/YYYY hh:mmA',
  // 12-hour format with AM/PM (1-digit month/day, 2-digit hour:minute)
  'M-D-YYYY hh:mmA',
  'M/D/YYYY hh:mmA',
  // 12-hour format with AM/PM (2-digit month/day, 1-digit hour)
  'MM-DD-YYYY h:mmA',
  'MM/DD/YYYY h:mmA',
  // 12-hour format with AM/PM (1-digit month/day, 1-digit hour)
  'M-D-YYYY h:mmA',
  'M/D/YYYY h:mmA',
  // 12-hour format with AM/PM (mixed 1 or 2 digit month and day)
  'M-DD-YYYY hh:mmA',
  'M-DD-YYYY h:mmA',
  'MM-D-YYYY hh:mmA',
  'MM-D-YYYY h:mmA',
  'M/DD/YYYY hh:mmA',
  'M/DD/YYYY h:mmA',
  'MM/D/YYYY hh:mmA',
  'MM/D/YYYY h:mmA'
];

const MultiFormatDateTimePicker: React.FC<MultiFormatDateTimePickerProps> = ({
  value,
  onChange,
  onBlur,
  format = 'MM-DD-YYYY hh:mmA',
  showTime,
  allowClear,
  style,
  name,
  placeholder,
  getPopupContainer,
  ...restProps
}) => {
  const [internalValue, setInternalValue] = useState<Moment | null>(value);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const popupContainerRef = useRef<HTMLDivElement>(null);
  const typedValueRef = useRef<string>('');
  const lastExternalValue = useRef<Moment | null>(value);
  const isDraggingFromOutsideRef = useRef(false);

  // Only sync external value to internal when it actually changes from outside
  useEffect(() => {
    const externalChanged =
      (value && !lastExternalValue.current) ||
      (!value && lastExternalValue.current) ||
      (value &&
        lastExternalValue.current &&
        !value.isSame(lastExternalValue.current));

    if (externalChanged) {
      setInternalValue(value);
      lastExternalValue.current = value;
      typedValueRef.current = value ? value.format(format) : '';
    }
  }, [value, format]);

  // Capture typed input value from the DOM input element
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const inputElement = container.querySelector('input');
    if (!inputElement) return undefined;

    const handleInput = (e: Event) => {
      const target = e.target as HTMLInputElement;
      typedValueRef.current = target.value;
    };

    inputElement.addEventListener('input', handleInput);
    return () => {
      inputElement.removeEventListener('input', handleInput);
    };
  }, []);

  const parseInputValue = useCallback((text: string): Moment | null => {
    if (!text || text.trim() === '') return null;

    // Try each accepted format
    const parsed = ACCEPTED_FORMATS.reduce<Moment | null>(
      (result, acceptedFormat) => {
        if (result) return result;
        const attemptParse = moment(text, acceptedFormat, true);
        return attemptParse.isValid() ? attemptParse : null;
      },
      null
    );

    if (parsed) return parsed;

    // Fallback to lenient parsing
    const fallbackParsed = moment(text);
    return fallbackParsed.isValid() ? fallbackParsed : null;
  }, []);

  const handleInputChange = (newValue: Moment | null) => {
    // When user selects from calendar popup or DatePicker parses successfully
    if (newValue?.isValid()) {
      setInternalValue(newValue);
      lastExternalValue.current = newValue;
      onChange(newValue);
      typedValueRef.current = newValue.format(format);
    } else if (!newValue) {
      // Cleared
      setInternalValue(null);
      lastExternalValue.current = null;
      onChange(null);
      typedValueRef.current = '';
    }
  };

  const handleInputBlur = () => {
    // Try to parse the typed value with multi-format on blur
    const typedValue = typedValueRef.current;
    if (typedValue && typedValue.trim() !== '') {
      const parsed = parseInputValue(typedValue);
      if (parsed) {
        setInternalValue(parsed);
        lastExternalValue.current = parsed;
        onChange(parsed);
        // Update the input display to the standard format
        const container = containerRef.current;
        if (container) {
          const inputElement = container.querySelector('input');
          if (inputElement) {
            inputElement.value = parsed.format(format);
            typedValueRef.current = parsed.format(format);
          }
        }
      }
    }

    if (onBlur) {
      onBlur();
    }
  };

  // Hide the OK button when popup opens (only within this component's popup container)
  useEffect(() => {
    if (!isOpen) return undefined;

    const popupContainer = popupContainerRef.current;
    if (!popupContainer) return undefined;

    const hideOkButton = () => {
      const okButton = popupContainer.querySelector('.syt-antd-picker-ok');
      if (okButton) {
        const element = okButton as HTMLElement;
        element.style.display = 'none';
      }
    };

    // Hide immediately and watch for DOM changes
    hideOkButton();

    const observer = new MutationObserver(() => {
      hideOkButton();
    });

    observer.observe(popupContainer, {
      childList: true,
      subtree: true
    });

    return () => {
      observer.disconnect();
    };
  }, [isOpen]);

  // Track whether mousedown originated outside the picker.
  useEffect(() => {
    const handleDocumentMouseDown = (e: MouseEvent) => {
      const isInsideContainer = containerRef.current?.contains(
        e.target as Node
      );
      const isInsidePopup = popupContainerRef.current?.contains(
        e.target as Node
      );
      isDraggingFromOutsideRef.current = !isInsideContainer && !isInsidePopup;
    };

    const handleDocumentMouseUp = () => {
      requestAnimationFrame(() => {
        isDraggingFromOutsideRef.current = false;
      });
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('mouseup', handleDocumentMouseUp);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('mouseup', handleDocumentMouseUp);
    };
  }, []);

  // Capture-phase interception: stop mouseup/click from reaching the
  // DatePicker's internal handlers (and any deferred focus() calls they
  // schedule) when the drag originated outside this component.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const blockIfDragging = (e: MouseEvent) => {
      if (isDraggingFromOutsideRef.current) {
        e.stopPropagation();
        e.preventDefault();
      }
    };

    container.addEventListener('mouseup', blockIfDragging, true);
    container.addEventListener('click', blockIfDragging, true);
    return () => {
      container.removeEventListener('mouseup', blockIfDragging, true);
      container.removeEventListener('click', blockIfDragging, true);
    };
  }, []);

  // Use our own popup container, or fall back to the provided one
  const handleGetPopupContainer = useCallback(
    (node: HTMLElement): HTMLElement => {
      if (popupContainerRef.current) {
        return popupContainerRef.current;
      }
      if (getPopupContainer) {
        return getPopupContainer(node);
      }
      return node.parentElement || document.body;
    },
    [getPopupContainer]
  );

  const handleInputFocus = () => {
    if (isDraggingFromOutsideRef.current) {
      containerRef.current?.querySelector('input')?.blur();
      return;
    }
    setIsOpen(true);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const parsedValue = (event.target as HTMLInputElement).value
        ? parseInputValue(event.currentTarget.value)
        : null;
      if (parsedValue) {
        handleInputChange(parsedValue);
      }
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ display: 'contents' }}>
      <div ref={popupContainerRef} style={{ position: 'relative' }} />
      <DatePicker
        data-testid={restProps['data-testid']}
        name={name}
        placeholder={placeholder}
        style={style}
        showTime={showTime}
        allowClear={allowClear}
        getPopupContainer={handleGetPopupContainer}
        format={format}
        value={internalValue}
        onSelect={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        open={isOpen}
        onOpenChange={setIsOpen}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};

export default MultiFormatDateTimePicker;
